import { defineStore } from "pinia";
import { useAppStore } from "./app.store";
import { useUserStore } from "./user.store";
import { computed, ref, shallowReactive, watch } from "vue";
import type { AbstractTrackingService } from "@/services/tracking/abstract-tracking-service";
import type { ChannelMetadata } from "@/models";
import { getChannelNameFromRoute } from "@/router/app.router";

/** Navigator interface extended with the Global Privacy Control property */
interface NavigatorWithGPC extends Navigator {
  globalPrivacyControl?: boolean;
}

export enum TrackingServiceKind {
  ESSENTIAL = "ESSENTIAL",
  MARKETING = "MARKETING",
  PERSONALIZATION = "PERSONALIZATION",
  ANALYTICS = "ANALYTICS",
}

export type TrackingServiceMap = Record<
  TrackingServiceKind,
  AbstractTrackingService[]
>;

export const useTrackingStore = defineStore("tracking", () => {
  const appStore = useAppStore();

  /**
   * CCPA "Do Not Sell" -- when true, marketing tracking must not be loaded.
   * Also respects the browser-level Global Privacy Control signal.
   * Uses lazy access to userStore to avoid circular dependency.
   */
  const ccpaDoNotSell = computed(() => {
    const userStore = useUserStore();
    return (
      userStore.user?.ccpa_do_not_sell === true ||
      (navigator as NavigatorWithGPC).globalPrivacyControl === true
    );
  });

  const hasServicesInitialized = ref(false);
  const servicesMap = shallowReactive<TrackingServiceMap>({
    [TrackingServiceKind.ESSENTIAL]: [],
    [TrackingServiceKind.MARKETING]: [],
    [TrackingServiceKind.PERSONALIZATION]: [],
    [TrackingServiceKind.ANALYTICS]: [],
  });

  function getInitialChannelMetadata(): ChannelMetadata {
    return {
      channelName: getChannelNameFromRoute(window.location.pathname) ?? "",
    };
  }

  function pushService(
    kind: keyof TrackingServiceMap,
    ...services: AbstractTrackingService[]
  ) {
    servicesMap[kind].push(...services);
  }

  function getService<T extends AbstractTrackingService>(
    ClassType: ClassType<T>,
  ): T | null {
    for (const service of Object.values(servicesMap)) {
      const foundService = service.find(
        (service) => service instanceof ClassType,
      );

      if (foundService) return foundService as T;
    }
    return null;
  }

  async function loadServices(kind: TrackingServiceKind) {
    for (let i = 0; i < servicesMap[kind].length; i++) {
      await servicesMap[kind][i]!.init(getInitialChannelMetadata());
      servicesMap[kind][i]!.initialized = true;
    }

    console.log(`[TrackingStore] Loaded ${kind} services`, servicesMap[kind]);
  }

  function getInitializedServices(): AbstractTrackingService[] {
    return Object.values(servicesMap)
      .flat()
      .filter((service) => service.initialized);
  }

  const trackingService: Omit<AbstractTrackingService, "initialized" | "init"> =
    {
      identifyUser: (...args) => {
        for (const service of getInitializedServices()) {
          service.identifyUser(...args);
        }
      },
      identifyChannel: (...args) => {
        for (const service of getInitializedServices()) {
          service.identifyChannel(...args);
        }
      },
      resetIdentity: () => {
        for (const service of getInitializedServices()) {
          service.resetIdentity();
          service.identifyChannel(getInitialChannelMetadata());
        }
      },
      track: (...args) => {
        for (const service of getInitializedServices()) {
          service.track(...args);
        }
      },
    };

  watch(
    () =>
      [
        appStore.cookieConsent,
        hasServicesInitialized.value,
        ccpaDoNotSell.value,
      ] as const,
    ([cookieConsent, hasServicesInitialized, doNotSell]) => {
      if (!hasServicesInitialized) {
        console.log("[TrackingStore] Services not initialized, skipping");
        return;
      }

      // CCPA: When "Do Not Sell" is enabled, do not load marketing tracking
      if (cookieConsent?.marketing && !doNotSell)
        loadServices(TrackingServiceKind.MARKETING);
      if (cookieConsent?.personalization)
        loadServices(TrackingServiceKind.PERSONALIZATION);
      if (cookieConsent?.analytics) loadServices(TrackingServiceKind.ANALYTICS);

      console.log(
        "[TrackingStore] Initialized tracking services with user preferences",
        cookieConsent,
        doNotSell ? "(CCPA Do Not Sell active)" : "",
      );
    },
    { immediate: true },
  );

  watch(hasServicesInitialized, (hasServicesInitialized) => {
    if (hasServicesInitialized) {
      console.log("[TrackingStore] Initializing essential services");
      loadServices(TrackingServiceKind.ESSENTIAL);
      console.log("[TrackingStore] Essential services initialized");
    }
  });

  return {
    identifyUser: trackingService.identifyUser,
    identifyChannel: trackingService.identifyChannel,
    resetIdentity: trackingService.resetIdentity,
    track: trackingService.track,
    getService,
    pushService,
    hasServicesInitialized,
  };
});
